import React, { createContext, useContext, useState, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { BACKEND_URL } from '../config';

// Initialize Stripe with publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const StripeContext = createContext(null);

export const useStripe = () => {
  const context = useContext(StripeContext);
  if (!context) {
    throw new Error('useStripe must be used within a StripeProvider');
  }
  return context;
};

export const StripeProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = BACKEND_URL;

  /**
   * Create a checkout session for subscription plans
   */
  const createSubscriptionCheckout = useCallback(async (planId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/stripe/create-subscription-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
      
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  /**
   * Create a checkout session for analysis pack purchases
   */
  const createAnalysisPackCheckout = useCallback(async (packId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/stripe/create-analysis-pack-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          pack_id: packId,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
      
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  /**
   * Check payment status after returning from Stripe
   */
  const checkPaymentStatus = useCallback(async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/stripe/checkout-status/${sessionId}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      return await response.json();
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [API_URL]);

  /**
   * Poll payment status with retries
   */
  const pollPaymentStatus = useCallback(async (sessionId, maxAttempts = 5, interval = 2000) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const status = await checkPaymentStatus(sessionId);
        
        if (status.payment_status === 'paid' || status.completed) {
          return { success: true, status };
        }
        
        if (status.status === 'expired') {
          return { success: false, error: 'Payment session expired' };
        }
        
        // Wait before next attempt
        await new Promise(resolve => setTimeout(resolve, interval));
      } catch (err) {
        if (attempt === maxAttempts - 1) {
          return { success: false, error: err.message };
        }
      }
    }
    
    return { success: false, error: 'Payment status check timed out' };
  }, [checkPaymentStatus]);

  /**
   * Get subscription status
   */
  const getSubscriptionStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/stripe/subscription-status`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to get subscription status');
      }

      return await response.json();
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [API_URL]);

  /**
   * Get payment history
   */
  const getPaymentHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/stripe/payment-history`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to get payment history');
      }

      return await response.json();
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [API_URL]);

  /**
   * Get pricing information
   */
  const getPricing = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/stripe/pricing`);

      if (!response.ok) {
        throw new Error('Failed to get pricing');
      }

      return await response.json();
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [API_URL]);

  const value = {
    stripePromise,
    loading,
    error,
    createSubscriptionCheckout,
    createAnalysisPackCheckout,
    checkPaymentStatus,
    pollPaymentStatus,
    getSubscriptionStatus,
    getPaymentHistory,
    getPricing
  };

  return (
    <StripeContext.Provider value={value}>
      {children}
    </StripeContext.Provider>
  );
};

export default StripeContext;
